// frontend/src/utils/formatters.jsx
//
// Locale-aware formatting and parsing for the SaaS dashboard.
//
// Single source of truth for how a number, date, or currency renders to
// the merchant. Drives every grid cell, KPI tile, chart label, and form
// in the SaaS app via `useFormatters` (the hook wrapper).
//
// The locale tag is the source of truth — we do NOT store separate
// decimal_separator / thousands_separator / date_format / time_format
// columns. `Intl.NumberFormat(locale, ...)` and `Intl.DateTimeFormat(...)`
// derive separators, date order, 12h-vs-24h clock from CLDR data using
// just the BCP 47 tag (e.g. 'de-DE').

import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';
import { enGB, enUS, es, nl, pl } from 'date-fns/locale';
import { enUS as pickersEnUS, esES, nlNL, plPL } from '@mui/x-date-pickers/locales';
import { periodLabelFields } from './chartPeriods';

// Locale assembly. `language` is a BCP 47 language (e.g. 'en', 'pl') and
// `region_code` is the ISO 3166-1 alpha-2 (e.g. 'US', 'GB'). We combine them
// into a full BCP 47 tag ('en-US', 'pl-PL') for Intl.* consumers — region
// drives CLDR's date order and 12h-vs-24h clock independently of language.
//
// Fallback chain: explicit user value → serializer's effective_* (which
// already does User → Store fallthrough server-side) → 'en' with no region.
// Both language columns hold a bare language code: the serializers store
// `core.models.app_language`'s answer and the field validators refuse
// anything else, so the region can be appended.
function _localeOf(user) {
  const lang = user?.language || user?.effective_language || 'en';
  const region = user?.region_code || user?.effective_region_code || '';
  return region ? `${lang}-${region}` : lang;
}

// The viewer's zone, for rendering a UTC timestamp as a wall-clock time or day.
//
// Two authoritative sources, in precedence order — a personal override, then
// the value the server already resolved through user → store → system default
// (`User.get_effective_timezone`). That chain cannot come back empty for a
// loaded user, so '' here means there is no user yet, and `formatDateTime` /
// `formatDate` render '-' for a timestamp rather than a guessed time or day.
function _timezoneOf(user) {
  return user?.timezone || user?.effective_timezone || '';
}

// Date-fns format string derived from the user's BCP 47 locale. MUI X's
// DatePicker takes a date-fns format token via its `format` prop; we build
// it from `Intl.DateTimeFormat.formatToParts()` so picker order and
// separators match the rest of the UI without us hard-coding per-locale
// strings. Year/month/day tokens map to date-fns; separator literals
// pass through. Fallback is en-US ('MM/dd/yyyy') if Intl returns
// unexpected parts.
export function getDateFnsFormat(user) {
  try {
    const parts = new Intl.DateTimeFormat(_localeOf(user), {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(2024, 0, 15));
    const out = parts.map((p) => {
      if (p.type === 'year') return 'yyyy';
      if (p.type === 'month') return 'MM';
      if (p.type === 'day') return 'dd';
      return p.value;
    }).join('');
    return out || 'MM/dd/yyyy';
  } catch {
    return 'MM/dd/yyyy';
  }
}

// The language a date picker speaks, for its LocalizationProvider:
// `adapterLocale` (date-fns) names the months and weekdays and starts the week
// on Monday — except English in the US, or with no region (the same en-US
// reading getDateFnsFormat gives it), where it starts on Sunday; `localeText`
// is MUI's own words (buttons, screen-reader labels). One entry per app
// language (core.models.LANGUAGE_CHOICES).
const pickerTexts = (muiLocale) => muiLocale.components.MuiLocalizationProvider.defaultProps.localeText;
const PICKER_LOCALES = {
  es: { adapterLocale: es, localeText: pickerTexts(esES) },
  nl: { adapterLocale: nl, localeText: pickerTexts(nlNL) },
  pl: { adapterLocale: pl, localeText: pickerTexts(plPL) },
};

export function getPickerLocale(user) {
  const [lang, region] = _localeOf(user).split('-');
  if (lang === 'en') {
    return { adapterLocale: !region || region === 'US' ? enUS : enGB, localeText: pickerTexts(pickersEnUS) };
  }
  const locale = PICKER_LOCALES[lang];
  if (!locale) throw new Error(`getPickerLocale: no date picker for app language '${lang}'`);
  return locale;
}

export function formatNumber(value, user, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat(_localeOf(user), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// Accepted inputs:
//   - 'YYYY-MM-DD'               (calendar date — `formatDate` only)
//   - ISO datetime with timezone ('YYYY-MM-DDTHH:mm:ss[.sss](Z|±HH:mm)')
//
// Explicitly rejected:
//   - A calendar date in `formatDateTime`: it names a day, not a moment, so
//     it has no time to show and no zone to convert from.
//   - Naive datetime strings like '2026-04-01T00:00:00' (no timezone). These
//     parse as the browser's LOCAL time under `parseISO` — a moment the
//     server never named, so the rendered day depends on whose machine
//     renders it.
//   - `Date` instances, for the same reason (locally-constructed Dates carry
//     a moment that can cross UTC day boundaries). Backend always sends ISO
//     strings — callers should hand those over directly, not parse into a
//     Date first.
//
// Same failure policy as `formatMonthYear`: console.error on misuse and
// render '-', so misuse surfaces loudly in dev without crashing the UI.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;

// `{ moment, isCalendarDate }` for an accepted input, else null — after
// logging why, unless the input is simply empty. A calendar date's moment is
// its UTC midnight, so reading it back in UTC gives the written day.
function _parseAcceptedDateInput(input, fnName) {
  if (input == null || input === '') return null;
  const isCalendarDate = typeof input === 'string' && DATE_ONLY_RE.test(input);
  const accepted = isCalendarDate || (typeof input === 'string' && ISO_DATETIME_RE.test(input));
  const moment = accepted ? parseISO(isCalendarDate ? `${input}T00:00:00Z` : input) : null;
  if (!moment || isNaN(moment.getTime())) {
    console.error(`${fnName} expects a date string (YYYY-MM-DD or ISO datetime with timezone), got:`, input);
    return null;
  }
  return { moment, isCalendarDate };
}

export function formatDate(date, user) {
  const parsed = _parseAcceptedDateInput(date, 'formatDate');
  if (!parsed) return '-';
  // A calendar date is a day, not a moment, so it reads as written in every
  // zone (a PO with NeedByDate=2026-04-24 reads '24' in the Americas too). A
  // timestamp is a moment, so its day is the viewer's — the zone
  // `formatDateTime` shows its time in, and like there, no zone renders '-'.
  const timeZone = parsed.isCalendarDate ? 'UTC' : _timezoneOf(user);
  if (!timeZone) return '-';
  // 2-digit day/month produces locale-natural numeric form: 04/03/2024
  // (en-US), 04.03.2024 (de-DE), 2024-03-04 (en-CA), etc.
  return new Intl.DateTimeFormat(_localeOf(user), {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsed.moment);
}

export function formatDateTime(date, user) {
  const parsed = _parseAcceptedDateInput(date, 'formatDateTime');
  if (!parsed) return '-';
  if (parsed.isCalendarDate) {
    console.error('formatDateTime expects a timestamp; a calendar date has no time — use formatDate. Got:', date);
    return '-';
  }
  // Timestamps render in the viewer's effective timezone (user → store →
  // system default, resolved server-side). 12h vs 24h is inherited from the
  // locale: en-US gives "03/04/2024, 02:30 PM"; de-DE gives "04.03.2024, 14:30".
  const timeZone = _timezoneOf(user);
  // No zone means no user yet. A time needs one to mean anything, so this
  // renders the same '-' as an unusable input rather than picking a clock —
  // for the seconds before the auth context lands, "no time" is honest and a
  // time in the wrong zone is not.
  if (!timeZone) return '-';
  return new Intl.DateTimeFormat(_localeOf(user), {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed.moment);
}

/**
 * Today's calendar date in `timeZone`, as the 'YYYY-MM-DD' wire string.
 * NOT a display helper — locale-agnostic on purpose, because the output is
 * sent to the server or compared against another wire date.
 *
 * The zone is always supplied by the caller, and for anything a merchant will
 * read as a business date that zone is the STORE's (`useStoreToday`), never
 * the viewer's. A receipt is booked against the store's calendar — the backend
 * stamps `store.get_local_time().date()` (`receive_goods`,
 * backend/purchase_orders/po_viewset.py) — and a due date is classified
 * against it (`core.date_utils.classify_due_status`).
 *
 * Returns '' when the zone isn't known, so a caller renders nothing and sends
 * nothing rather than a day from a second clock: an unknown zone has no right
 * answer, and a plausible wrong date is worse than a blank field.
 */
export function todayInTimeZone(timeZone) {
  if (!timeZone) return '';
  return formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
}

export function parseLocaleNumber(value, user) {
  if (value === '' || value === null || value === undefined) return NaN;
  // Numeric input short-circuit. AG Grid's valueParser → valueSetter chain
  // commonly hands the setter an already-parsed number; running String()
  // on 0.5 yields "0.5", and an EU-locale parse of "0.5" treats the dot
  // as a thousands separator and returns 5. Trust the number we were
  // given instead of round-tripping it through its US-style string form.
  if (typeof value === 'number') return value;
  let str = String(value).trim();
  if (str === '') return NaN;

  // Derive decimal/thousands separators from the user's locale via Intl.
  // formatToParts(1234.5) returns the actual glyphs CLDR uses for this
  // locale, so we never need to store them as separate columns.
  const parts = new Intl.NumberFormat(_localeOf(user)).formatToParts(1234.5);
  const decSep = parts.find(p => p.type === 'decimal')?.value || '.';
  const thousSep = parts.find(p => p.type === 'group')?.value || '';

  if (decSep !== '.') {
    // Locale uses comma (or other) as decimal separator — strip thousands
    // grouping then swap decimal to '.' so parseFloat can read it.
    if (thousSep) str = str.split(thousSep).join('');
    str = str.replace(decSep, '.');
  } else {
    // Standard dot-decimal: strip commas and whitespace as thousands
    // separators (covers en-US ',' and locales that use thin space).
    str = str.replace(/[,\s]/g, '');
  }

  return parseFloat(str);
}

export function formatCompact(value, currency, user) {
  if (value == null) return '-';
  const v = parseFloat(value);
  if (isNaN(v)) return '-';
  const opts = currency
    ? { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }
    : { notation: 'compact', maximumFractionDigits: 1 };
  return new Intl.NumberFormat(_localeOf(user), opts).format(v);
}

// Strict 'YYYY-MM' / 'YYYY-MM-DD' parser. Date inputs are rejected — a
// locally-constructed Date for "day 1 of month X" lands in the previous UTC
// month for positive-offset timezones and produces a wrong label under the
// UTC-based formatter below. Callers must hand over a date-only string.
// If you hold a Date, slice to 'YYYY-MM' via its UTC accessors first.
const MONTH_YEAR_RE = /^(\d{4})-(0[1-9]|1[0-2])(?:-\d{2})?$/;

export function formatMonthYear(dateStr, user) {
  if (dateStr == null || dateStr === '') return '-';
  if (typeof dateStr !== 'string' || !MONTH_YEAR_RE.test(dateStr)) {
    // Fail loud in development; render '-' in prod so a bad cell doesn't
    // take the whole page down.
    console.error('formatMonthYear expects a YYYY-MM or YYYY-MM-DD string, got:', dateStr);
    return '-';
  }
  // Parse explicitly as UTC; Intl.DateTimeFormat with timeZone: 'UTC' then
  // yields the expected month regardless of the host's local offset.
  const iso = dateStr.length === 7 ? `${dateStr}-01T00:00:00Z` : `${dateStr}T00:00:00Z`;
  const dateObj = new Date(iso);
  if (isNaN(dateObj.getTime())) return '-';
  return new Intl.DateTimeFormat(_localeOf(user), {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric',
  }).format(dateObj);
}

/**
 * Label one bucket of a chart's time axis in the merchant's locale —
 * 'Aug 2026' for a month, '12 Aug' / 'Aug 12' / '12 sie' for a week or day.
 * Which fields each bucket carries, and why, is `periodLabelFields`.
 *
 * Takes the axis value (a Date) rather than a wire string, which every other
 * date helper here refuses: AG Charts hands a label formatter the plotted x
 * value, and that value came from `periodToDate`, so it is a UTC instant by
 * construction. Formatting it back in UTC is what keeps a month bucket from
 * reading as the last day of the previous month east of Greenwich — the
 * hazard the string-only contract exists to prevent.
 *
 * One formatter covers both the tick labels and the tooltip heading: with no
 * chart-level `formatter` option set, AG Charts resolves a datum's axis text
 * through the axis label formatter (`formatDatum` → `formatAxisLabelValue`,
 * ag-charts-community 14.0.2), so the hovered period reads exactly like the
 * tick beneath it.
 */
export function formatPeriodLabel(date, bucket, user) {
  return new Intl.DateTimeFormat(_localeOf(user), {
    timeZone: 'UTC',
    ...periodLabelFields(bucket),
  }).format(date);
}

export function formatCurrency(value, currency, user) {
  if (value === null || value === undefined || isNaN(value)) return '-';
  if (!currency) return formatNumber(value, user, 2);
  return new Intl.NumberFormat(_localeOf(user), {
    style: 'currency',
    currency,
  }).format(value);
}
